import React, { useState, useEffect } from 'react';
import { Download, X, Monitor, Smartphone } from 'lucide-react';

let deferredPrompt = null;

// Capture the beforeinstallprompt event globally
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event('pwa-installable'));
  });
}

export const usePwaInstall = () => {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setIsInstalled(true); setCanInstall(false); };

    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
    return result.outcome === 'accepted';
  };

  return { canInstall, isInstalled, install };
};

// Floating install banner (shows on landing page for new visitors)
export const PwaInstallBanner = () => {
  const { canInstall, isInstalled, install } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (canInstall && !dismissed && !isInstalled) {
      const wasDismissed = sessionStorage.getItem('pwa_banner_dismissed');
      if (!wasDismissed) {
        const t = setTimeout(() => setVisible(true), 3000);
        return () => clearTimeout(t);
      }
    }
  }, [canInstall, dismissed, isInstalled]);

  if (!visible || isInstalled) return null;

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
    sessionStorage.setItem('pwa_banner_dismissed', '1');
  };

  const handleInstall = async () => {
    const accepted = await install();
    if (accepted) setVisible(false);
  };

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 z-[9997] animate-in" data-testid="pwa-install-banner">
      <div className="bg-[var(--color-charcoal)] text-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold mb-0.5">Install Stilos</h4>
              <p className="text-[10px] text-white/60 leading-relaxed">
                Add to your home screen for a faster, native-like experience. Works offline too.
              </p>
            </div>
            <button onClick={handleDismiss} className="p-1 hover:bg-white/10 rounded-lg transition-colors" data-testid="pwa-banner-dismiss">
              <X className="w-4 h-4 text-white/40" />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 h-9 bg-white text-[var(--color-charcoal)] text-xs font-semibold rounded-xl hover:bg-white/90 transition-colors flex items-center justify-center gap-1.5"
              data-testid="pwa-install-btn"
            >
              <Download className="w-3.5 h-3.5" /> Install App
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 h-9 text-white/60 text-xs rounded-xl hover:bg-white/10 transition-colors"
              data-testid="pwa-banner-later"
            >
              Later
            </button>
          </div>
        </div>
        <div className="px-4 py-2 bg-white/5 flex items-center justify-center gap-4 text-[9px] text-white/40">
          <span className="flex items-center gap-1"><Monitor className="w-3 h-3" /> Desktop</span>
          <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> Mobile</span>
          <span>No app store needed</span>
        </div>
      </div>
    </div>
  );
};

// Compact install button for sidebar/settings
export const PwaInstallButton = ({ className = '' }) => {
  const { canInstall, isInstalled, install } = usePwaInstall();

  if (isInstalled) {
    return (
      <div className={`flex items-center gap-2 text-[10px] text-emerald-600 ${className}`} data-testid="pwa-installed-badge">
        <Download className="w-3.5 h-3.5" />
        <span>App installed</span>
      </div>
    );
  }

  if (!canInstall) return null;

  return (
    <button
      onClick={install}
      className={`flex items-center gap-2 text-xs font-medium text-[var(--color-coral)] hover:text-[var(--color-charcoal)] transition-colors ${className}`}
      data-testid="pwa-sidebar-install-btn"
    >
      <Download className="w-3.5 h-3.5" />
      <span>Install App</span>
    </button>
  );
};
